/**
 * Responsibility: describe each theme park ride as geometry.
 *
 * ## The three shapes that make a fairground, and the rest
 *
 * A park is identified from the air by **a wheel, a peaked tent, and a lift hill**. Nothing else on a real
 * fairground has a profile anyone would recognise at distance: the stalls, halls and pavilions are low
 * boxes, and their job is to be the dense low mass those three stand out of. Every decision here follows
 * from that split — the three landmarks get segments spent on them, the filler is kept cheap.
 *
 * ## Two kinds of output, for the reason the industrial site has two
 *
 * - **Solids** are ring stacks, derived into lines by `ringStack.js`, and they are **masked**: a tent is
 *   canvas and a carousel roof is a lid, so their near faces must hide their far ones.
 * - **Extras** are loose segments and are never masked: a wheel rim, a coaster track and a lattice mast are
 *   open steelwork, and masking an open frame makes it occlude like a slab.
 *
 * The same descriptor shape as `buildingShapes.js`, so both catalogues and, later, both placers can treat
 * them identically.
 *
 * ## Nothing here moves
 *
 * A wheel with gondolas hanging plumb and a swing ride with its chairs flung outward are both static
 * geometry. The brief rules out motion, not the *shape* of motion, and a chair swing standing still with
 * its chains vertical looks broken rather than calm.
 *
 * Contains no Three.js and no DOM, so it is fully unit testable.
 */

import {
  groundRing,
  linesForSolid,
  polygonFootprint,
  rectFootprint,
  ringAt,
  segmentAt,
} from './ringStack.js';
import { PARK } from '../config.js';

/**
 * Normalise a spec into the values every ride needs.
 *
 * `height` is the gradient's reference span rather than any particular dimension: rides differ wildly in
 * height and each one states what its own colour ramp should run over, so a 13-unit stall is not drawn
 * entirely in the darkest colour beside a 258-unit tower.
 */
function resolve(spec) {
  const baseY = spec.baseY ?? 0;

  return {
    centerX: spec.centerX ?? 0,
    centerZ: spec.centerZ ?? 0,
    baseY,
    facing: spec.facing ?? 0,
    groundAt: spec.sampleBaseY ?? (() => baseY),
  };
}

/**
 * A frame rotated about the vertical axis at a ride's centre.
 *
 * Every ride is authored along its own local X axis and then turned, so nothing in this file has to think
 * about world orientation. `along` runs through the ride, `across` runs sideways.
 */
function frameFor({ centerX, centerZ, facing }) {
  const cos = Math.cos(facing);
  const sin = Math.sin(facing);

  return {
    /** A ground position in the ride's own frame. */
    at: (along, across) => [centerX + cos * along - sin * across, centerZ + sin * along + cos * across],

    /** A full point, with a height. */
    point: (along, across, y) => [
      centerX + cos * along - sin * across,
      y,
      centerZ + sin * along + cos * across,
    ],
  };
}

/** A segment between two `[x, y, z]` points, with one gradient fraction per end. */
function line(a, b, t1, t2 = t1) {
  return segmentAt(a[0], a[1], a[2], b[0], b[1], b[2], t1, t2);
}

/** A gradient fraction from an absolute height, clamped so a ride's overhang cannot exceed the ramp. */
function fractionOf(y, baseY, span) {
  const t = span > 0 ? (y - baseY) / span : 0;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

// ---------------------------------------------------------------------------
// The ferris wheel
// ---------------------------------------------------------------------------

/**
 * A ferris wheel: two rims either side of a hub, spokes, hanging gondolas, and A-frame legs.
 *
 * **The one vertical circle in the world.** Everything else built here is a box, a cone, a cylinder or a
 * line, so the shape carries the whole biome and none of it is masked — a wheel is a bicycle wheel, and
 * masking it would fill the circle in.
 *
 * The gondolas matter more than they look: hanging plumb below the rim is what makes it a *wheel* rather
 * than a gear, and it is the detail that survives to fog distance.
 */
function ferrisWheelExtras(spec) {
  const { baseY, groundAt } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const radius = PARK.WHEEL_RADIUS;
  const hubY = baseY + PARK.WHEEL_CLEARANCE + radius;
  const span = hubY + radius - baseY;
  const t = (y) => fractionOf(y, baseY, span);

  const sides = PARK.WHEEL_RIM_SIDES;
  const offsets = [-PARK.WHEEL_RIM_OFFSET, PARK.WHEEL_RIM_OFFSET];

  /** A point on the rim at a fraction of the way round, on one of the two rims. */
  const rim = (turn, across, r = radius) => {
    const angle = turn * Math.PI * 2;
    return frame.point(Math.cos(angle) * r, across, hubY + Math.sin(angle) * r);
  };

  const out = [];

  // The two rims, and the ties between them that make the wheel a drum rather than a disc.
  for (const across of offsets) {
    for (let i = 0; i < sides; i += 1) {
      out.push(line(rim(i / sides, across), rim((i + 1) / sides, across), t(rim(i / sides, across)[1])));
    }
  }

  for (let i = 0; i < PARK.WHEEL_SPOKES; i += 1) {
    const turn = i / PARK.WHEEL_SPOKES;

    for (const across of offsets) {
      const hub = frame.point(0, across, hubY);
      const outer = rim(turn, across);
      out.push(line(hub, outer, t(hubY), t(outer[1])));
    }

    // Rim to rim, at each spoke.
    out.push(line(rim(turn, offsets[0]), rim(turn, offsets[1]), t(rim(turn, 0)[1])));

    /*
      A gondola, hanging plumb. Drawn as a bar under the rim and a small box below it — four segments each,
      which across twelve spokes is a quarter of the wheel's cost and worth every one of them.
    */
    const anchor = rim(turn, 0);
    const top = [anchor[0], anchor[1] - PARK.WHEEL_GONDOLA_DROP, anchor[2]];
    const half = PARK.WHEEL_GONDOLA_SIZE / 2;

    out.push(line(anchor, top, t(anchor[1]), t(top[1])));

    const corners = [
      [top[0] - half, top[1], top[2] - half],
      [top[0] + half, top[1], top[2] - half],
      [top[0] + half, top[1], top[2] + half],
      [top[0] - half, top[1], top[2] + half],
    ];

    for (let c = 0; c < 4; c += 1) {
      out.push(line(corners[c], corners[(c + 1) % 4], t(top[1])));
    }

    const floorY = top[1] - PARK.WHEEL_GONDOLA_SIZE * 0.6;
    for (const corner of corners) {
      out.push(line(corner, [corner[0], floorY, corner[2]], t(top[1]), t(floorY)));
    }
  }

  // The hub itself, so the spokes converge on something.
  for (const across of offsets) {
    for (let i = 0; i < 6; i += 1) {
      out.push(line(rim(i / 6, across, PARK.WHEEL_HUB_RADIUS), rim((i + 1) / 6, across, PARK.WHEEL_HUB_RADIUS), t(hubY)));
    }
  }

  /*
    A-frame legs on both sides, splayed fore and aft. Two legs per side rather than one: a single post per
    side reads as a signpost, and the splay is what carries the weight visually.
  */
  const spread = radius * PARK.WHEEL_LEG_SPREAD;

  for (const across of offsets) {
    for (const along of [-spread, spread]) {
      const [footX, footZ] = frame.at(along, across);
      const foot = [footX, groundAt(footX, footZ), footZ];
      const hub = frame.point(0, across, hubY);
      out.push(line(foot, hub, t(foot[1]), t(hubY)));
    }

    // A cross brace, which is what makes two legs an A-frame.
    const [leftX, leftZ] = frame.at(-spread * 0.5, across);
    const [rightX, rightZ] = frame.at(spread * 0.5, across);
    const braceY = baseY + PARK.WHEEL_CLEARANCE + radius * 0.35;
    out.push(line([leftX, braceY, leftZ], [rightX, braceY, rightZ], t(braceY)));
  }

  return out;
}

// ---------------------------------------------------------------------------
// The big top
// ---------------------------------------------------------------------------

/** Half the tent's span at a peak's position, used by both the wall and the cones. */
function tentSpan() {
  return { halfLength: PARK.TENT_LENGTH / 2, halfWidth: PARK.TENT_WIDTH / 2 };
}

/** Where each peak stands along the tent's long axis. */
function tentPeakOffsets() {
  const { halfLength } = tentSpan();
  const peaks = Math.max(1, PARK.TENT_PEAKS);
  if (peaks === 1) return [0];

  // Inset from the ends, so the end cones' skirts land on the wall rather than past it.
  const reach = halfLength * 0.62;
  return Array.from({ length: peaks }, (_, i) => -reach + (2 * reach * i) / (peaks - 1));
}

/**
 * The tent: an oval wall, and one cone per peak.
 *
 * **Three overlapping cones, not one clever surface.** A real three-pole big top is exactly that — canvas
 * pulled up over three king poles, sagging into valleys between them — and building it from three ring
 * stacks means the depth mask comes free and correct, where a hand-built tent surface would need its own.
 */
function bigTopSolids(spec) {
  const { centerX, centerZ, baseY, groundAt } = resolve(spec);
  const frame = frameFor(resolve(spec));
  const { halfLength, halfWidth } = tentSpan();

  const eaveY = baseY + PARK.TENT_EAVE_HEIGHT;
  const peakY = baseY + PARK.TENT_PEAK_HEIGHT;

  const solids = [];

  /*
    The wall, as an oval. Built in the ride's own frame by rotating an axis-aligned ellipse, so the tent
    turns with everything else.
  */
  const wallFootprint = polygonFootprint(0, 0, halfLength, halfWidth, PARK.TENT_WALL_SIDES).map(
    ([along, across]) => frame.at(along, across),
  );

  solids.push({ rings: [groundRing(wallFootprint, groundAt), ringAt(wallFootprint, eaveY)] });

  const coneRadius = halfWidth * PARK.TENT_CONE_SPREAD;

  for (const along of tentPeakOffsets()) {
    const [coneX, coneZ] = frame.at(along, 0);
    const footprint = polygonFootprint(coneX, coneZ, coneRadius, coneRadius, PARK.TENT_CONE_SIDES);

    solids.push({ rings: [ringAt(footprint, eaveY)], apex: [coneX, peakY, coneZ] });
  }

  return solids;
}

/** The valance and the pennants: the two details that make canvas read as canvas. */
function bigTopExtras(spec) {
  const { baseY } = resolve(spec);
  const frame = frameFor(resolve(spec));
  const { halfLength, halfWidth } = tentSpan();

  const eaveY = baseY + PARK.TENT_EAVE_HEIGHT;
  const peakY = baseY + PARK.TENT_PEAK_HEIGHT;
  const span = PARK.TENT_PEAK_HEIGHT + PARK.TENT_FLAG_HEIGHT;
  const t = (y) => fractionOf(y, baseY, span);

  const out = [];

  /*
    A scalloped valance around the eave: each span of the wall polygon dips in the middle. **The cheapest
    detail in the park and the one that does the most** — a plain eave ring reads as a shed roof, and the
    same ring with a dip in every span reads as fabric.
  */
  const corners = polygonFootprint(0, 0, halfLength, halfWidth, PARK.TENT_WALL_SIDES);
  const dipY = eaveY - PARK.TENT_VALANCE_DROP;

  for (let i = 0; i < corners.length; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

    const from = frame.point(a[0], a[1], eaveY);
    const dip = frame.point(mid[0], mid[1], dipY);
    const to = frame.point(b[0], b[1], eaveY);

    out.push(line(from, dip, t(eaveY), t(dipY)));
    out.push(line(dip, to, t(dipY), t(eaveY)));
  }

  // A pennant on each peak, which is the one place a fairground is allowed a flourish.
  for (const along of tentPeakOffsets()) {
    const base = frame.point(along, 0, peakY);
    const tip = frame.point(along, 0, peakY + PARK.TENT_FLAG_HEIGHT);
    const fly = frame.point(along + PARK.TENT_FLAG_LENGTH, 0, peakY + PARK.TENT_FLAG_HEIGHT * 0.62);

    out.push(line(base, tip, t(peakY), t(tip[1])));
    out.push(line(tip, fly, t(tip[1]), t(fly[1])));
    out.push(line(fly, [base[0], base[1] + PARK.TENT_FLAG_HEIGHT * 0.34, base[2]], t(fly[1]), t(peakY)));
  }

  return out;
}

// ---------------------------------------------------------------------------
// Roller coasters
// ---------------------------------------------------------------------------

/**
 * Draw a track from a path function: two rails, cross ties, and supports to the ground.
 *
 * One builder for every coaster, because a coaster *is* its path — the circuit and the looper differ only
 * in the function passed in here, which is the whole reason a second variant was almost free.
 *
 * @param {object} spec
 * @param {(t: number) => [number, number, number]} path - Position at `t` from 0 to 1, in world coordinates.
 * @param {boolean} closed - Whether the end joins the start.
 */
function coasterTrack(spec, path, closed) {
  const { baseY, groundAt } = resolve(spec);
  const span = PARK.COASTER_LIFT_HEIGHT;
  const t = (y) => fractionOf(y, baseY, span);

  const steps = PARK.COASTER_STEPS;
  const half = PARK.COASTER_GAUGE / 2;

  /**
   * Position at a node index.
   *
   * **A closed circuit has `steps` distinct nodes, not `steps + 1`.** Treating the last as its own node put
   * `path(1)` and `path(0)` at the same place, which produced a zero-length tie and a tangent of zero — and a
   * zero tangent collapses both rails onto the centre line. Wrapping the index instead makes the join
   * ordinary.
   */
  const nodeAt = (i) => path((closed ? ((i % steps) + steps) % steps : i) / steps);

  /**
   * The two rail positions at a node.
   *
   * Offset perpendicular to the path *in plan*, not in three dimensions. Banking the rails properly would
   * need a frame carried along the curve, and at this gauge nobody could see the difference — but a
   * vertical loop would make a 3D normal flip over, which would put a visible twist in the track.
   */
  const railsAt = (i) => {
    const here = nodeAt(i);

    // At the open end there is no node ahead, so the tangent comes from behind instead.
    const trailing = !closed && i >= steps;
    const from = trailing ? nodeAt(i - 1) : here;
    const to = trailing ? here : nodeAt(i + 1);

    const dx = to[0] - from[0];
    const dz = to[2] - from[2];
    const length = Math.hypot(dx, dz) || 1;

    const nx = -dz / length;
    const nz = dx / length;

    return [
      [here[0] + nx * half, here[1], here[2] + nz * half],
      [here[0] - nx * half, here[1], here[2] - nz * half],
    ];
  };

  const out = [];

  // Nodes, and the edges between them. A closed circuit's last edge wraps to node zero.
  const nodes = closed ? steps : steps + 1;

  for (let i = 0; i < nodes; i += 1) {
    const here = railsAt(i);

    if (closed || i < nodes - 1) {
      const ahead = railsAt(i + 1);
      for (let rail = 0; rail < 2; rail += 1) {
        out.push(line(here[rail], ahead[rail], t(here[rail][1]), t(ahead[rail][1])));
      }
    }

    // Cross ties, which are what make two parallel lines read as a track.
    if (i % PARK.COASTER_TIE_EVERY === 0) {
      out.push(line(here[0], here[1], t(here[0][1])));
    }

    /*
      A support column down to the ground, with a footer. Without these the track floats, and a floating
      curve reads as a ribbon rather than as a structure.
    */
    if (i % PARK.COASTER_SUPPORT_EVERY === 0) {
      const centre = nodeAt(i);
      const ground = groundAt(centre[0], centre[2]);

      if (centre[1] - ground > PARK.COASTER_GAUGE) {
        out.push(line(centre, [centre[0], ground, centre[2]], t(centre[1]), t(ground)));

        // A splayed foot, so the column is planted rather than balanced.
        for (const [dx, dz] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ]) {
          const reach = PARK.COASTER_GAUGE * 0.8;
          out.push(
            line(
              [centre[0], ground + reach, centre[2]],
              [centre[0] + dx * reach, ground, centre[2] + dz * reach],
              t(ground + reach),
              t(ground),
            ),
          );
        }
      }
    }
  }

  return out;
}

/**
 * A closed circuit: lift hill, first drop, a run of decaying humps, and banked turns back to the station.
 *
 * The plan is an oval so the ride returns to where it started, which is what a coaster does and what stops
 * the shape reading as a bridge.
 */
function coasterCircuitExtras(spec) {
  const { baseY } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const halfLength = PARK.COASTER_LENGTH / 2;
  const halfWidth = PARK.COASTER_WIDTH / 2;
  const lift = PARK.COASTER_LIFT_HEIGHT;

  /** Height at a fraction of the way round the circuit. */
  const heightAt = (u) => {
    // Lift hill: a straight climb out of the station.
    if (u < 0.24) return baseY + lift * (u / 0.24);

    // The first drop, steepest thing on the ride.
    if (u < 0.34) {
      const k = (u - 0.24) / 0.1;
      return baseY + lift * (1 - k * k);
    }

    /*
      Decaying humps. Each is lower than the last, which is what a coaster losing energy looks like — equal
      humps read as a corrugated roof.
    */
    const k = (u - 0.34) / (1 - 0.34);
    const humps = Math.abs(Math.sin(k * Math.PI * PARK.COASTER_HUMPS));
    return baseY + lift * PARK.COASTER_HUMP_SCALE * humps * (1 - k);
  };

  const path = (u) => {
    const angle = u * Math.PI * 2;
    const [x, z] = frame.at(Math.cos(angle) * halfLength, Math.sin(angle) * halfWidth);
    return [x, heightAt(u), z];
  };

  return coasterTrack(spec, path, true);
}

/**
 * An out-and-back with a vertical loop.
 *
 * **The loop is the point.** A circuit reads as a coaster; a loop reads as a coaster from much further
 * away, because a closed vertical circle is a shape nothing else in this world makes. The track advances
 * slowly along its axis through the loop so the two ends do not sit exactly on top of each other.
 */
function coasterLoopExtras(spec) {
  const { baseY } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const halfLength = PARK.COASTER_LENGTH / 2;
  const lift = PARK.COASTER_LIFT_HEIGHT;
  const loopRadius = PARK.COASTER_LOOP_RADIUS;

  const path = (u) => {
    // Lift hill, climbing along the ride's axis.
    if (u < 0.26) {
      const k = u / 0.26;
      return frame.point(-halfLength + halfLength * 0.75 * k, 0, baseY + lift * k);
    }

    // The drop into the loop's entry.
    if (u < 0.4) {
      const k = (u - 0.26) / 0.14;
      const along = -halfLength * 0.25 + halfLength * 0.35 * k;
      return frame.point(along, 0, baseY + lift * (1 - k * k) * (1 - k) + loopRadius * 0.2);
    }

    // The loop: a full turn in the vertical plane, drifting forward as it goes.
    if (u < 0.62) {
      const k = (u - 0.4) / 0.22;
      const angle = -Math.PI / 2 + k * Math.PI * 2;
      const along = halfLength * 0.1 + k * loopRadius * 0.9 + Math.sin(angle) * loopRadius;
      return frame.point(along, 0, baseY + loopRadius * 0.25 + loopRadius + Math.cos(angle + Math.PI) * loopRadius);
    }

    // Run-out: two shrinking humps to the brake run.
    const k = (u - 0.62) / 0.38;
    const humps = Math.abs(Math.sin(k * Math.PI * 2));
    return frame.point(
      halfLength * 0.2 + halfLength * 0.8 * k,
      0,
      baseY + lift * PARK.COASTER_HUMP_SCALE * humps * (1 - k),
    );
  };

  return coasterTrack(spec, path, false);
}

// ---------------------------------------------------------------------------
// The drop tower
// ---------------------------------------------------------------------------

/**
 * A lattice mast with a car partway up and a crown at the top.
 *
 * The tallest thing in the park, and unmasked: the bracing *is* the structure, so there is nothing for a
 * mask to represent. Four legs alone read as a mast; the same four legs with crossed diagonals read as a
 * ride, which is the cheapest possible way to say so — the transmission pylons settled this already.
 */
function dropTowerExtras(spec) {
  const { baseY, groundAt } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const height = PARK.TOWER_HEIGHT;
  const half = PARK.TOWER_HALF_WIDTH;
  const panels = PARK.TOWER_PANELS;
  const t = (y) => fractionOf(y, baseY, height);

  /**
   * One of the four legs at a height fraction.
   *
   * At the bottom the leg starts on the *terrain* rather than at the nominal base, so a mast on a slope is
   * planted at all four corners. Adding a separate foot stub instead would emit a zero-length segment
   * wherever the ground happened to be level, which is most of the time — `groundRing` solves the same
   * problem the same way for solids.
   */
  const leg = (corner, k) => {
    const signAlong = corner === 0 || corner === 3 ? -1 : 1;
    const signAcross = corner < 2 ? -1 : 1;
    const [x, z] = frame.at(half * signAlong, half * signAcross);

    return [x, k === 0 ? groundAt(x, z) : baseY + height * k, z];
  };

  const out = [];

  for (let p = 0; p < panels; p += 1) {
    const lower = p / panels;
    const upper = (p + 1) / panels;

    for (let corner = 0; corner < 4; corner += 1) {
      const from = leg(corner, lower);
      const to = leg(corner, upper);
      out.push(line(from, to, t(from[1]), t(to[1])));

      const next = (corner + 1) % 4;
      out.push(line(leg(corner, lower), leg(next, upper), t(from[1]), t(to[1])));
      out.push(line(leg(next, upper), leg(next, lower), t(to[1]), t(from[1])));
    }
  }

  /*
    The car: a ring around the mast with seats sticking out. It is what makes a lattice mast a *ride* rather
    than a transmission tower, and it has to be well below the top or the tower looks finished.
  */
  const carY = baseY + height * PARK.TOWER_CAR_AT;
  const reach = PARK.TOWER_CAR_REACH;
  const seats = 8;

  for (let i = 0; i < seats; i += 1) {
    const angle = (i / seats) * Math.PI * 2;
    const next = ((i + 1) / seats) * Math.PI * 2;

    const here = frame.point(Math.cos(angle) * reach, Math.sin(angle) * reach, carY);
    const ahead = frame.point(Math.cos(next) * reach, Math.sin(next) * reach, carY);

    out.push(line(here, ahead, t(carY)));
    out.push(line(here, [here[0], carY - reach * 0.5, here[2]], t(carY), t(carY - reach * 0.5)));
  }

  // A crown, so the mast terminates in something rather than just stopping.
  const crownY = baseY + height;
  const crown = PARK.TOWER_CROWN_RADIUS;

  for (let i = 0; i < 8; i += 1) {
    const angle = (i / 8) * Math.PI * 2;
    const next = ((i + 1) / 8) * Math.PI * 2;

    const here = frame.point(Math.cos(angle) * crown, Math.sin(angle) * crown, crownY - crown * 0.6);
    const ahead = frame.point(Math.cos(next) * crown, Math.sin(next) * crown, crownY - crown * 0.6);

    out.push(line(here, ahead, t(here[1])));
    out.push(line(here, frame.point(0, 0, crownY), t(here[1]), t(crownY)));
  }

  return out;
}

// ---------------------------------------------------------------------------
// The chair swing
// ---------------------------------------------------------------------------

/** The canopy: a cone, and the only masked part of the ride. */
function swingSolids(spec) {
  const { centerX, centerZ, baseY } = resolve(spec);

  const eaveY = baseY + PARK.SWING_HEIGHT;
  const footprint = polygonFootprint(centerX, centerZ, PARK.SWING_CANOPY_RADIUS, PARK.SWING_CANOPY_RADIUS, PARK.SWING_CANOPY_SIDES);

  return [{ rings: [ringAt(footprint, eaveY)], apex: [centerX, eaveY + PARK.SWING_CANOPY_RISE, centerZ] }];
}

/**
 * The mast and the chairs, flung outward.
 *
 * **Static, but shaped by motion.** Chains hanging plumb would read as broken rather than calm; flung out
 * they read as a ride that is running, without anything actually moving. That distinction — no motion, but
 * the shape of motion — is what makes a still fairground look alive.
 */
function swingExtras(spec) {
  const { centerX, centerZ, baseY, groundAt } = resolve(spec);

  const mastTop = baseY + PARK.SWING_HEIGHT;
  const span = PARK.SWING_HEIGHT + PARK.SWING_CANOPY_RISE;
  const t = (y) => fractionOf(y, baseY, span);

  const out = [];

  out.push(line([centerX, groundAt(centerX, centerZ), centerZ], [centerX, mastTop, centerZ], t(baseY), t(mastTop)));

  const rimY = mastTop - PARK.SWING_CANOPY_RISE * 0.2;
  const seatY = mastTop - PARK.SWING_DROP;

  for (let i = 0; i < PARK.SWING_CHAINS; i += 1) {
    const angle = (i / PARK.SWING_CHAINS) * Math.PI * 2;

    const from = [
      centerX + Math.cos(angle) * PARK.SWING_CANOPY_RADIUS * 0.9,
      rimY,
      centerZ + Math.sin(angle) * PARK.SWING_CANOPY_RADIUS * 0.9,
    ];

    const seat = [
      centerX + Math.cos(angle) * PARK.SWING_REACH,
      seatY,
      centerZ + Math.sin(angle) * PARK.SWING_REACH,
    ];

    out.push(line(from, seat, t(rimY), t(seatY)));

    // A seat: a short bar, which is all that is needed for a chain to be carrying something.
    const bar = PARK.SWING_CANOPY_RADIUS * 0.14;
    out.push(
      line(
        [seat[0] - Math.sin(angle) * bar, seat[1], seat[2] + Math.cos(angle) * bar],
        [seat[0] + Math.sin(angle) * bar, seat[1], seat[2] - Math.cos(angle) * bar],
        t(seatY),
      ),
    );
  }

  return out;
}

// ---------------------------------------------------------------------------
// The carousel
// ---------------------------------------------------------------------------

/** Platform, drum and conical roof — all masked, because a carousel is a solid lid on a solid disc. */
function carouselSolids(spec) {
  const { centerX, centerZ, baseY, groundAt } = resolve(spec);

  const radius = PARK.CAROUSEL_RADIUS;
  const sides = PARK.CAROUSEL_SIDES;
  const footprint = polygonFootprint(centerX, centerZ, radius, radius, sides);

  const platformY = baseY + PARK.CAROUSEL_PLATFORM_HEIGHT;
  const eaveY = baseY + PARK.CAROUSEL_EAVE_HEIGHT;
  const peakY = baseY + PARK.CAROUSEL_PEAK_HEIGHT;

  return [
    // The platform: a low disc the whole ride stands on.
    { rings: [groundRing(footprint, groundAt), ringAt(footprint, platformY)] },

    // The roof, slightly oversailing so it reads as a lid rather than a cap.
    {
      rings: [ringAt(polygonFootprint(centerX, centerZ, radius * 1.05, radius * 1.05, sides), eaveY)],
      apex: [centerX, peakY, centerZ],
    },
  ];
}

/** The poles, and the finial that stops the roof reading as a gazebo. */
function carouselExtras(spec) {
  const { centerX, centerZ, baseY } = resolve(spec);

  const radius = PARK.CAROUSEL_RADIUS;
  const platformY = baseY + PARK.CAROUSEL_PLATFORM_HEIGHT;
  const eaveY = baseY + PARK.CAROUSEL_EAVE_HEIGHT;
  const peakY = baseY + PARK.CAROUSEL_PEAK_HEIGHT;
  const span = PARK.CAROUSEL_PEAK_HEIGHT + PARK.CAROUSEL_FINIAL_HEIGHT;
  const t = (y) => fractionOf(y, baseY, span);

  const out = [];

  for (let i = 0; i < PARK.CAROUSEL_POLES; i += 1) {
    const angle = (i / PARK.CAROUSEL_POLES) * Math.PI * 2;
    const x = centerX + Math.cos(angle) * radius * 0.88;
    const z = centerZ + Math.sin(angle) * radius * 0.88;

    out.push(line([x, platformY, z], [x, eaveY, z], t(platformY), t(eaveY)));
  }

  out.push(line([centerX, peakY, centerZ], [centerX, peakY + PARK.CAROUSEL_FINIAL_HEIGHT, centerZ], t(peakY), t(1)));

  return out;
}

// ---------------------------------------------------------------------------
// The low mass: stalls, a hall, and the gate
// ---------------------------------------------------------------------------

/** Where each stall in a row stands, along the row's own axis. */
function stallOffsets() {
  const pitch = PARK.STALL_WIDTH + PARK.STALL_GAP;
  const count = Math.max(1, PARK.STALL_COUNT);
  return Array.from({ length: count }, (_, i) => (i - (count - 1) / 2) * pitch);
}

/** A row of midway stalls: small boxes, masked, standing shoulder to shoulder. */
function stallRowSolids(spec) {
  const { baseY, groundAt } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const halfWidth = PARK.STALL_WIDTH / 2;
  const halfDepth = PARK.STALL_DEPTH / 2;
  const roofY = baseY + PARK.STALL_HEIGHT;

  return stallOffsets().map((along) => {
    const footprint = [
      frame.at(along - halfWidth, -halfDepth),
      frame.at(along + halfWidth, -halfDepth),
      frame.at(along + halfWidth, halfDepth),
      frame.at(along - halfWidth, halfDepth),
    ];

    return { rings: [groundRing(footprint, groundAt), ringAt(footprint, roofY)] };
  });
}

/**
 * The awnings.
 *
 * **What turns a row of boxes into a midway.** The boxes alone are a terrace of sheds; the same boxes with
 * a sloped awning over each front are stalls, and it costs three segments each.
 */
function stallRowExtras(spec) {
  const { baseY } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const halfWidth = PARK.STALL_WIDTH / 2;
  const halfDepth = PARK.STALL_DEPTH / 2;
  const roofY = baseY + PARK.STALL_HEIGHT;
  const lipY = roofY - PARK.STALL_AWNING_DROP;
  const reach = halfDepth + PARK.STALL_AWNING_REACH;
  const t = (y) => fractionOf(y, baseY, PARK.STALL_HEIGHT);

  const out = [];

  for (const along of stallOffsets()) {
    const left = frame.point(along - halfWidth, reach, lipY);
    const right = frame.point(along + halfWidth, reach, lipY);

    out.push(line(left, right, t(lipY)));
    out.push(line(frame.point(along - halfWidth, halfDepth, roofY), left, t(roofY), t(lipY)));
    out.push(line(frame.point(along + halfWidth, halfDepth, roofY), right, t(roofY), t(lipY)));

    // A scallop in the awning's lip, the same trick as the tent's valance at a tenth of the size.
    out.push(
      line(
        frame.point(along, reach, lipY - PARK.STALL_AWNING_DROP * 0.5),
        left,
        t(lipY - PARK.STALL_AWNING_DROP * 0.5),
        t(lipY),
      ),
    );
    out.push(
      line(
        frame.point(along, reach, lipY - PARK.STALL_AWNING_DROP * 0.5),
        right,
        t(lipY - PARK.STALL_AWNING_DROP * 0.5),
        t(lipY),
      ),
    );
  }

  return out;
}

/** A wide low hall for bumper cars or an arcade: the park's largest floor area and lowest profile. */
function hallSolids(spec) {
  const { baseY, groundAt } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const halfWidth = PARK.HALL_WIDTH / 2;
  const halfDepth = PARK.HALL_DEPTH / 2;
  const eaveY = baseY + PARK.HALL_EAVE_HEIGHT;

  const footprint = [
    frame.at(-halfWidth, -halfDepth),
    frame.at(halfWidth, -halfDepth),
    frame.at(halfWidth, halfDepth),
    frame.at(-halfWidth, halfDepth),
  ];

  /*
    A ridge as a second ring of zero depth. A gable roof is two rings, the upper one collapsed to a line —
    which the ring stack draws and masks correctly with no special case, the same way the setback tower's
    shelf works.
  */
  const ridge = [
    frame.at(-halfWidth, 0),
    frame.at(halfWidth, 0),
    frame.at(halfWidth, 0),
    frame.at(-halfWidth, 0),
  ];

  return [
    {
      rings: [
        groundRing(footprint, groundAt),
        ringAt(footprint, eaveY),
        ringAt(ridge, baseY + PARK.HALL_PEAK_HEIGHT),
      ],
    },
  ];
}

/** The hall's scalloped eave, matching the tent's so the two read as the same fairground. */
function hallExtras(spec) {
  const { baseY } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const halfWidth = PARK.HALL_WIDTH / 2;
  const halfDepth = PARK.HALL_DEPTH / 2;
  const eaveY = baseY + PARK.HALL_EAVE_HEIGHT;
  const dipY = eaveY - PARK.HALL_VALANCE_DROP;
  const t = (y) => fractionOf(y, baseY, PARK.HALL_PEAK_HEIGHT);

  const out = [];
  const bays = 6;

  for (const across of [-halfDepth, halfDepth]) {
    for (let i = 0; i < bays; i += 1) {
      const from = -halfWidth + (PARK.HALL_WIDTH * i) / bays;
      const to = -halfWidth + (PARK.HALL_WIDTH * (i + 1)) / bays;
      const mid = (from + to) / 2;

      out.push(line(frame.point(from, across, eaveY), frame.point(mid, across, dipY), t(eaveY), t(dipY)));
      out.push(line(frame.point(mid, across, dipY), frame.point(to, across, eaveY), t(dipY), t(eaveY)));
    }
  }

  return out;
}

/** The entrance towers, masked, either side of the gate. */
function gateSolids(spec) {
  const { baseY, groundAt } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const half = PARK.GATE_SPAN / 2;
  const width = PARK.GATE_TOWER_WIDTH;
  const roofY = baseY + PARK.GATE_TOWER_HEIGHT;

  return [-half, half].map((along) => {
    const footprint = rectFootprint(0, 0, width, width).map(([x, z]) => frame.at(along + x, z));
    return { rings: [groundRing(footprint, groundAt), ringAt(footprint, roofY)], apex: null };
  });
}

/**
 * The arch and sign between the towers.
 *
 * A park needs a front. An arch over a gap is the oldest way of saying "the thing starts here", and it is
 * what turns a scatter of rides into a place with an inside and an outside.
 */
function gateExtras(spec) {
  const { baseY } = resolve(spec);
  const frame = frameFor(resolve(spec));

  const half = PARK.GATE_SPAN / 2;
  const towerTop = baseY + PARK.GATE_TOWER_HEIGHT;
  const crest = towerTop + PARK.GATE_ARCH_RISE;
  const t = (y) => fractionOf(y, baseY, PARK.GATE_TOWER_HEIGHT + PARK.GATE_ARCH_RISE);

  const out = [];
  const steps = PARK.GATE_ARCH_STEPS;

  /** A point on the arch: a parabola from one tower top to the other. */
  const arch = (k, depth) => {
    const along = -half + PARK.GATE_SPAN * k;
    const lift = PARK.GATE_ARCH_RISE * (1 - Math.pow(2 * k - 1, 2));
    return frame.point(along, depth, towerTop + lift);
  };

  // Two parallel arches with ties between them, so the sign has thickness.
  for (const depth of [-PARK.GATE_SIGN_DEPTH / 2, PARK.GATE_SIGN_DEPTH / 2]) {
    for (let i = 0; i < steps; i += 1) {
      const from = arch(i / steps, depth);
      const to = arch((i + 1) / steps, depth);
      out.push(line(from, to, t(from[1]), t(to[1])));
    }
  }

  for (let i = 0; i <= steps; i += 2) {
    out.push(line(arch(i / steps, -PARK.GATE_SIGN_DEPTH / 2), arch(i / steps, PARK.GATE_SIGN_DEPTH / 2), t(arch(i / steps, 0)[1])));
  }

  // A crest marker at the top, which is where a real park puts its name.
  out.push(line(arch(0.5, 0), frame.point(0, 0, crest + PARK.GATE_ARCH_RISE * 0.4), t(crest), t(1)));

  return out;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * Turn a ride's solids and extras into line segments.
 *
 * The single place lines are derived, so no ride can draw something its mask does not cover.
 */
function buildFrom(descriptor, spec) {
  const { baseY } = resolve(spec);
  const span = descriptor.span;
  const out = [];

  if (descriptor.solids) {
    for (const solid of descriptor.solids(spec)) {
      out.push(...linesForSolid(solid, baseY, span));
    }
  }

  if (descriptor.extras) out.push(...descriptor.extras(spec));

  return out;
}

/**
 * Every ride, as a dispatch table matching `buildingShapes.js`.
 *
 * `span` is the height its colour gradient runs over — not a footprint, and not always its own height. A
 * park mixes a 13-unit stall with a 258-unit tower, and a single shared span would draw everything short
 * entirely in the darkest end of the ramp.
 *
 * `masked: false` means the ride casts no depth mask. The wheel, the coasters and the drop tower are all
 * open steelwork: there is no surface for a mask to represent, and masking one would make it occlude like
 * a slab — the lattice tower in the city registry settled this.
 */
const DESCRIPTORS = [
  {
    id: 'ferrisWheel',
    label: 'Ferris wheel',
    reference: 'the shape that says fairground',
    extras: ferrisWheelExtras,
    span: PARK.WHEEL_CLEARANCE + PARK.WHEEL_RADIUS * 2,
    masked: false,
  },
  {
    id: 'bigTop',
    label: 'Big top, three poles',
    reference: 'a travelling circus tent',
    solids: bigTopSolids,
    extras: bigTopExtras,
    span: PARK.TENT_PEAK_HEIGHT + PARK.TENT_FLAG_HEIGHT,
  },
  {
    id: 'coaster',
    label: 'Coaster circuit',
    reference: 'a wooden out-and-back',
    extras: coasterCircuitExtras,
    span: PARK.COASTER_LIFT_HEIGHT,
    masked: false,
  },
  {
    id: 'coasterLoop',
    label: 'Coaster with a loop',
    reference: 'a steel looper',
    extras: coasterLoopExtras,
    span: PARK.COASTER_LIFT_HEIGHT,
    masked: false,
  },
  {
    id: 'dropTower',
    label: 'Drop tower',
    reference: 'a gyro drop',
    extras: dropTowerExtras,
    span: PARK.TOWER_HEIGHT,
    masked: false,
  },
  {
    id: 'swingRide',
    label: 'Chair swing',
    reference: 'a wave swinger',
    solids: swingSolids,
    extras: swingExtras,
    span: PARK.SWING_HEIGHT + PARK.SWING_CANOPY_RISE,
  },
  {
    id: 'carousel',
    label: 'Carousel',
    reference: 'a merry-go-round',
    solids: carouselSolids,
    extras: carouselExtras,
    span: PARK.CAROUSEL_PEAK_HEIGHT + PARK.CAROUSEL_FINIAL_HEIGHT,
  },
  {
    id: 'stallRow',
    label: 'Midway stalls',
    reference: 'a row of game booths',
    solids: stallRowSolids,
    extras: stallRowExtras,
    span: PARK.STALL_HEIGHT,
  },
  {
    id: 'hall',
    label: 'Bumper car hall',
    reference: 'a fairground pavilion',
    solids: hallSolids,
    extras: hallExtras,
    span: PARK.HALL_PEAK_HEIGHT,
  },
  {
    id: 'gate',
    label: 'Entrance arch',
    reference: 'the front of any park',
    solids: gateSolids,
    extras: gateExtras,
    span: PARK.GATE_TOWER_HEIGHT + PARK.GATE_ARCH_RISE,
  },
];

/** The registry, each entry given a `build` and a resolved `masked` flag. */
export const PARK_SHAPES = DESCRIPTORS.map((descriptor) => ({
  ...descriptor,
  masked: descriptor.masked !== false,
  build: (spec) => buildFrom(descriptor, spec),
  solidsFor: (spec) => (descriptor.solids ? descriptor.solids(spec) : []),
}));

/**
 * Look up a ride by id, falling back to the wheel.
 *
 * Falls back rather than throwing, for the same reason the building and aircraft registries do: an id
 * naming a shape that has since been renamed should degrade, not break a world.
 */
export function getParkShapeOrDefault(id) {
  return PARK_SHAPES.find((shape) => shape.id === id) ?? PARK_SHAPES[0];
}

/**
 * Segment count for a ride at a given spec.
 *
 * Measured by building it rather than derived by formula, for the reason `segmentsForTree` needed a test:
 * a second formula to keep in step with the emitter is exactly the kind of duplication that goes quietly
 * wrong.
 */
export function countParkSegments(shape, spec) {
  return shape.build(spec).length;
}
